import { List } from 'immutable';
import moment from 'moment';

import { parseDateStringToDate } from 'utils/dates';

import CrudModel from './CrudModel';
import ProductRatesModel from './ProductRatesModel';
import FamilyRatesModel from './FamilyRatesModel';
import CompanyRatesModel from './CompanyRatesModel';

const getSelectValue = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'object') return value.value ?? value.id ?? null;
    return value;
};

const BACKEND_DATE_FORMATS = ['DD/MM/YYYY HH:mm:ss', 'DD/MM/YYYY HH:mm', 'DD/MM/YYYY'];
const SERVER_DATE_FORMAT = 'DD/MM/YYYY HH:mm:ss';

const parseDateForCrudField = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    const parsed = parseDateStringToDate(value);
    if (parsed instanceof Date && !Number.isNaN(parsed.getTime())) {
        return parsed;
    }

    return null;
};

const formatDateForEntity = (value) => {
    if (!value) return null;
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : moment(value).format(SERVER_DATE_FORMAT);
    }
    if (typeof value === 'string') {
        const parsed = moment(value, BACKEND_DATE_FORMATS, true);
        if (parsed.isValid()) {
            return parsed.format(SERVER_DATE_FORMAT);
        }
    }
    return null;
};

const isBackendRateEntity = (entity, backendIdKey) =>
    entity &&
    typeof entity === 'object' &&
    entity[backendIdKey] != null &&
    entity[backendIdKey] !== '';

const toModelArray = (list, ModelClass, backendIdKey) => {
    if (!list) return [];
    const items = list.toArray ? list.toArray() : list;
    return items.map((item) => {
        if (item instanceof ModelClass) return item;
        const entity = item?.entity ?? item;
        const fromBackend = isBackendRateEntity(entity, backendIdKey);
        return new ModelClass(entity, fromBackend);
    });
};

const mapRatesFromSource = (items, ModelClass) => {
    if (!items) return [];
    const list = items.toArray ? items.toArray() : items;
    return list.map((item) => {
        if (item instanceof ModelClass) return item;
        return new ModelClass(item, true);
    });
};

export default class RatesModel extends CrudModel {
    static fromEntityCrudData(data) {
        if (!data) return new RatesModel({}, [], [], []);

        const entity = {
            id: data.id,
            name: data.name ?? data.Name ?? '',
            description: data.description ?? data.Description ?? '',
            idcurrency: getSelectValue(data.idCurrency ?? data.idcurrency),
            startdate: formatDateForEntity(data.startDate ?? data.startdate),
            enddate: formatDateForEntity(data.endDate ?? data.enddate),
            stridenvironment: data.strIdEnvironment ?? data.stridenvironment ?? null,
            oldname: data.oldName ?? data.oldname,
            currencysymbol: data.currencysymbol ?? data.currencySymbol,
        };

        return new RatesModel(
            entity,
            toModelArray(data.productrates, ProductRatesModel, 'idproduct'),
            toModelArray(data.familyrates, FamilyRatesModel, 'idfamily'),
            toModelArray(data.companyrates, CompanyRatesModel, 'idcompany'),
        );
    }

    static toCrud({ data }) {
        const crudData =
            data instanceof RatesModel ? RatesModel.toEntityCrudData(data) : { ...data };

        // Keep idCurrency as a plain id so the value-list field resolves its label
        // from `tblCurrency` (same source as the dropdown). Building the label here
        // from the backend currency string made editing show a different label than
        // selecting from the list.
        const idCurrencyValue = getSelectValue(crudData.idCurrency ?? crudData.idcurrency);
        const startDateValue = parseDateForCrudField(crudData.startDate ?? crudData.startdate);
        const endDateValue = parseDateForCrudField(crudData.endDate ?? crudData.enddate);

        return {
            ...crudData,
            idCurrency: idCurrencyValue ?? undefined,
            startDate: startDateValue,
            endDate: endDateValue,
            productrates: List.isList(crudData.productrates)
                ? crudData.productrates
                : List(crudData.productrates || []),
            familyrates: List.isList(crudData.familyrates)
                ? crudData.familyrates
                : List(crudData.familyrates || []),
            companyrates: List.isList(crudData.companyrates)
                ? crudData.companyrates
                : List(crudData.companyrates || []),
        };
    }

    static toEntityCrudData(ratesModel) {
        const entity = ratesModel.entity || {};
        return {
            id: entity.id || undefined,
            name: entity.name || '',
            description: entity.description || '',
            idCurrency: entity.idCurrency ?? entity.idcurrency ?? null,
            startDate: parseDateForCrudField(entity.startDate ?? entity.startdate),
            endDate: parseDateForCrudField(entity.endDate ?? entity.enddate),
            strIdEnvironment: entity.strIdEnvironment ?? entity.stridenvironment ?? null,
            productrates: List(ratesModel.productRates),
            familyrates: List(ratesModel.familyRates),
            companyrates: List(ratesModel.companyRates),
            currencysymbol: entity.currencysymbol,
            oldName: entity.oldname || entity.oldName,
        };
    }

    static toList({ data }) {
        return data.map((current) => {
            const endDateValue = current.endDate ?? current.enddate ?? '';
            const startDateValue = current.startDate ?? current.startdate ?? '';

            return {
                rawData: { ...current },
                ...current,
                startDate: startDateValue,
                endDate: endDateValue,
            };
        });
    }

    constructor(entity, productRates, familyRates, companyRates) {
        super(entity);
        this.productRates = mapRatesFromSource(productRates, ProductRatesModel);
        this.familyRates = mapRatesFromSource(familyRates, FamilyRatesModel);
        this.companyRates = mapRatesFromSource(companyRates, CompanyRatesModel);
    }

    get crud() {
        let defaultCrud = super.crud;

        if (this.id !== '' && this.id !== '-1') {
            defaultCrud = defaultCrud.set('oldName', this.name);
        }

        defaultCrud = defaultCrud.set('productrates', List(this.productRates));
        defaultCrud = defaultCrud.set('familyrates', List(this.familyRates));
        defaultCrud = defaultCrud.set('companyrates', List(this.companyRates));
        return defaultCrud;
    }

    get saveCrud() {
        const entity = this.entity;
        const crud = {
            id: entity.id ? entity.id : '-1',
            name: entity.name,
            description: entity.description,
            idcurrency: entity.idcurrency,
            startdate: entity.startdate,
            enddate: entity.enddate,
            // idsucursal: entity.idsucursal,
            strIdEnvironment: entity.stridenvironment,
            products: [],
            families: [],
            accounts: [],
        };
        this.productRates.forEach((productRate) => {
            crud.products.push(productRate.saveCrud);
        });
        this.familyRates.forEach((familyRate) => {
            crud.families.push(familyRate.saveCrud);
        });
        this.companyRates.forEach((companyRate) => {
            crud.accounts.push(companyRate.saveCrud);
        });
        return crud;
    }

    get id() {
        return this.entity.id;
    }

    get name() {
        return this.entity.name;
    }

    get description() {
        return this.entity.description;
    }

    get idCurrency() {
        return this.entity.idCurrency;
    }

    get startDate() {
        const value = this.entity.startdate ?? this.entity.startDate;
        if (!value) return '';
        const parsed = moment(value, BACKEND_DATE_FORMATS, true);
        return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '';
    }

    get endDate() {
        const value = this.entity.enddate ?? this.entity.endDate;
        if (!value) return '';
        const parsed = moment(value, BACKEND_DATE_FORMATS, true);
        return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '';
    }

    get idSucursal() {
        return this.entity.strIdEnvironment;
    }

    get strIdEnvironment() {
        return this.entity.strIdEnvironment;
    }
}
